/* eslint-disable @typescript-eslint/no-deprecated -- DEPRECATION: [APPDEV-17834] BREAKING-CHANGE: migrate to ChartTooltip before next major. */
import { CopyIcon } from '@dynatrace/strato-icons';
import { format, formatDate, units } from '@dynatrace-sdk/units';

import { useCopyToClipboard } from '../../../../core/utils/useCopyToClipboard.js';
import { ChartSeriesAction } from '../../../core/components/series-action/ChartSeriesAction.js';
import { ChartTooltip } from '../../../core/components/tooltip/ChartTooltip/ChartTooltip.js';
import { DEFAULT_GANTT_SEGMENT_NAME } from '../../constants/defaults.js';
import { useGanttSegmentsContext } from '../../context/GanttSegmentsContext.js';
import { getEntryColor } from '../../functions/get-entry-color.js';
import type { GanttSegmentData } from '../../types/gantt-chart.js';

export const GanttChartTooltipBody = ({
  entry,
  row,
  parent,
  isDate,
}: {
  entry: GanttSegmentData;
  row: GanttSegmentData[];
  parent?: GanttSegmentData[];
  isDate: boolean;
}) => {
  const {
    config: {
      nameAccessor = 'name',
      formatter,
      tooltipActions,
      colorAccessor,
      colorPalette,
    },
  } = useGanttSegmentsContext();
  const duration =
    entry.end !== undefined ? entry.end - entry.start : undefined;
  const name = (entry[nameAccessor] || DEFAULT_GANTT_SEGMENT_NAME) as string;

  const [copyName] = useCopyToClipboard(name);
  const [copyStart] = useCopyToClipboard(`${entry.start}`);
  const [copyEnd] = useCopyToClipboard(`${entry.end}`);
  const [copyDuration] = useCopyToClipboard(`${duration}`);

  const formatValue = isDate ? formatDate : format;
  return (
    <ChartTooltip.SeriesItem
      color={getEntryColor(entry, colorAccessor, colorPalette)}
      name={name}
      actions={
        tooltipActions
          ? tooltipActions(entry, row, parent)
          : () => (
              <ChartSeriesAction>
                <ChartSeriesAction.Item onSelect={copyName}>
                  {name}
                  <ChartSeriesAction.ItemIcon>
                    <CopyIcon />
                  </ChartSeriesAction.ItemIcon>
                </ChartSeriesAction.Item>
                <ChartSeriesAction.Item onSelect={copyStart}>
                  Start: {formatValue(entry.start, formatter)}
                  <ChartSeriesAction.ItemIcon>
                    <CopyIcon />
                  </ChartSeriesAction.ItemIcon>
                </ChartSeriesAction.Item>
                {entry.end && (
                  <ChartSeriesAction.Item onSelect={copyEnd}>
                    End: {formatValue(entry.end, formatter)}
                    <ChartSeriesAction.ItemIcon>
                      <CopyIcon />
                    </ChartSeriesAction.ItemIcon>
                  </ChartSeriesAction.Item>
                )}
                {duration && (
                  <ChartSeriesAction.Item onSelect={copyDuration}>
                    Duration:{' '}
                    {format(
                      duration,
                      isDate && !formatter
                        ? { input: units.time.millisecond }
                        : formatter,
                    )}
                    <ChartSeriesAction.ItemIcon>
                      <CopyIcon />
                    </ChartSeriesAction.ItemIcon>
                  </ChartSeriesAction.Item>
                )}
              </ChartSeriesAction>
            )
      }
    />
  );
};
